// src/components/install/IdeInstallMenu.tsx
//
// The one-click MCP install surface. Renders a tab strip across every supported
// IDE (sourced from IDE_REGISTRY in registry order, VS Code pre-selected by
// default) and, for the active IDE, an InstallButton plus the target config file
// path and token note.
//
// When showManualFallback is set (the default) it ALWAYS renders the rendered
// Config_JSON as a selectable copy block — including for deep-link IDEs — so
// developers in locked-down environments where the protocol handler is blocked
// still have a manual escape hatch. For Claude Code the `claude mcp add` CLI
// one-liner is shown as well when present.

import { useState, useEffect } from 'react'
import { Copy, Check } from 'lucide-react'
import { InstallButton } from './InstallButton'
import { buildInstallTarget, IDE_REGISTRY, type IdeDescriptor, type IdeId } from '../../lib/ideInstall'
import type { CatalogEntry } from '../../lib/catalog'
import { resolveMcpBaseUrl } from '../../lib/mcpConfig'

interface IdeInstallMenuProps {
  entry: CatalogEntry
  baseUrl?: string
  ides?: IdeDescriptor[]
  defaultIde?: IdeId
  showManualFallback?: boolean
}

/** For local-command entries, show the user-level config path instead of repo-level. */
function _localConfigPath(ide: IdeDescriptor): string {
  switch (ide.id) {
    case 'vscode':
    case 'vscode-insiders':
      return 'VS Code User Settings JSON (Ctrl+Shift+P → Open User Settings)'
    case 'kiro':
      return 'C:\\Users\\<you>\\.kiro\\settings\\mcp.json (User-level)'
    case 'cursor':
      return 'C:\\Users\\<you>\\.cursor\\mcp.json (User-level)'
    case 'claude':
      return 'C:\\Users\\<you>\\.claude\\settings.json (User-level)'
    default:
      return ide.configFilePath
  }
}

/** For local-command entries, show token instructions relevant to the tool (not sdlcToken). */
function _localTokenNote(ide: IdeDescriptor): string {
  switch (ide.id) {
    case 'vscode':
    case 'vscode-insiders':
      return 'Prompted on first connect via ${input:...} and stored in VS Code secret storage.'
    case 'kiro':
      return 'Paste your actual token directly into C:\\Users\\<you>\\.kiro\\settings\\mcp.json (user-level, never committed).'
    case 'cursor':
    case 'claude':
      return 'Paste your actual token directly into the user-level config file (never committed).'
    default:
      return ide.tokenNote
  }
}

/** Selectable, copy-to-clipboard code block mirroring the QuickStart CopyBlock. */
function CopyBlock({ code, label }: Readonly<{ code: string; label?: string }>) {
  const [copied, setCopied] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      {label && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>
          {label}
        </div>
      )}
      <pre style={{
        background: 'var(--surface-hover)', padding: 'var(--space-2)',
        borderRadius: 'var(--radius-s)', fontSize: 12, overflow: 'auto',
        border: '1px solid var(--border)', margin: 0, lineHeight: 1.6,
      }}>
        {code}
      </pre>
      <button
        onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
        style={{
          position: 'absolute', top: label ? 28 : 8, right: 8,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
          color: 'var(--text-muted)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        {copied ? <Check size={12} color="var(--success)" /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

export function IdeInstallMenu({
  entry,
  baseUrl = resolveMcpBaseUrl(),
  ides = IDE_REGISTRY as IdeDescriptor[],
  defaultIde = 'vscode',
  showManualFallback = true,
}: Readonly<IdeInstallMenuProps>) {
  const initial = ides.find(i => i.id === defaultIde)?.id ?? ides[0]?.id
  const [activeId, setActiveId] = useState<IdeId | undefined>(initial)
  const [userEmail, setUserEmail] = useState('')

  // Fetch logged-in user's email to prefill USERNAME fields
  useEffect(() => {
    const BASE = import.meta.env.VITE_API_URL ?? ''
    fetch(`${BASE}/api/v1/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u?.email) setUserEmail(u.email) })
      .catch(() => {})
  }, [])

  const activeIde = ides.find(i => i.id === activeId) ?? ides[0]
  if (!activeIde) return null

  // For local-command entries, prefill USERNAME env vars with user's email
  // and replace token placeholders with a clear readable placeholder
  const resolvedEntry = entry.serverKind === 'local-command' && entry.env
    ? {
        ...entry,
        env: Object.fromEntries(
          Object.entries(entry.env).map(([k, v]) => {
            if (k.includes('USERNAME') && v.startsWith('${input:')) {
              return [k, userEmail || '<REPLACE_WITH_YOUR_EMAIL>']
            }
            if (v.startsWith('${input:') || v.startsWith('${')) {
              return [k, '<REPLACE_WITH_YOUR_TOKEN>']
            }
            return [k, v]
          })
        ),
      }
    : entry

  const target = buildInstallTarget(activeIde, resolvedEntry, baseUrl)

  return (
    <div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 'var(--space-2)' }}>
        {ides.map(ide => (
          <button
            key={ide.id}
            onClick={() => setActiveId(ide.id)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none',
              borderBottom: activeIde.id === ide.id ? '2px solid var(--brand)' : '2px solid transparent',
              color: activeIde.id === ide.id ? 'var(--brand)' : 'var(--text-muted)',
            }}
          >{ide.label}</button>
        ))}
      </div>

      <div style={{ marginBottom: 'var(--space-2)' }}>
        <InstallButton ide={activeIde} entry={entry} baseUrl={baseUrl} />
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
        <strong>Config file:</strong> <code>{entry.serverKind === 'local-command' ? _localConfigPath(activeIde) : activeIde.configFilePath}</code>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 'var(--space-2)', whiteSpace: 'pre-line' }}>
        <strong>Token:</strong>{' '}
        {entry.serverKind === 'local-command'
          ? _localTokenNote(activeIde)
          : activeIde.tokenNote === 'See token setup instructions below.'
            ? <>Set <code>SDLC_TOKEN</code> env var before launching IDE, then restart. <a href="/quick-start#get-platform-token" style={{ color: 'var(--brand)' }}>Token setup guide →</a></>
            : activeIde.tokenNote
        }
      </div>

      {showManualFallback && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <CopyBlock
            label={`${entry.serverKind === 'local-command' ? _localConfigPath(activeIde) : activeIde.configFilePath} (manual setup)`}
            code={target.configJson}
          />
          {target.cliCommand && (
            <div style={{ marginTop: 'var(--space-2)' }}>
              <CopyBlock label="Or add it via the CLI" code={target.cliCommand} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
